import { useMemo } from 'react';

import { useMultiFlightMap } from '../../MultiFlightMap.context';
import { toId } from '../domain/groupIds';

import type { CartographyItemInfo } from '../domain/cartographyGrouping';
import type { GroupFamily } from '../domain/grouping.types';
import type { CartographyTags, LatLng } from '../services/cartographyGrouping.service';

/** How an item of any family is shown in the grouping modals (plus what cartographic grouping needs). */
export interface GroupingItem extends CartographyItemInfo {
  detail: string;
  /** Ids of every group the item references (visibility is resolved by the caller). */
  groupIds: string[];
  id: string;
  /** Venue position, to tag venues that have no cartography tags yet. */
  latlng?: LatLng;
  name: string;
  tags?: CartographyTags;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const groupIdsOf = (groups: unknown[] | undefined): string[] => (groups ?? []).map(toId);

/** Non-deleted items of a family, keyed by their UI id, ready to be listed in the grouping modals. */
export function useGroupingItems(family: GroupFamily): GroupingItem[] {
  const { temporaryLocation, temporaryScreens, temporaryVenues, temporaryZones } = useMultiFlightMap();

  return useMemo(() => {
    switch (family) {
      case 'screens':
        return temporaryScreens
          .filter(screen => !screen.deleted)
          .map(screen => ({
            detail: text(screen.device_id),
            groupIds: groupIdsOf(screen.groups),
            id: toId(screen.source_element_id),
            name: text(screen.name) || text(screen.device_id),
            tags: screen.tags
          }));
      case 'venues':
        return temporaryVenues
          .filter(venue => !venue.deleted)
          .map(venue => ({
            country: venue.country,
            detail: text(venue.address),
            groupIds: groupIdsOf(venue.groups),
            id: toId(venue._id),
            latlng: venue.latlng,
            name: text(venue.name),
            tags: venue.tags
          }));
      default:
        return (
          temporaryZones
            .filter(zone => !zone.deleted)
            // The cartography area being previewed is not part of the list yet
            .filter(zone => !temporaryLocation?.name || zone.cartographyLevelId !== temporaryLocation.name)
            .map(zone => ({
              detail: text(zone.address),
              groupIds: groupIdsOf(zone.groups),
              id: toId(zone._id),
              name: text(zone.name)
            }))
        );
    }
  }, [family, temporaryLocation, temporaryScreens, temporaryVenues, temporaryZones]);
}
